/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel, NotificationModel } from '../../../../../core/models';
import { Injectable, OnDestroy } from '@angular/core';
import { LanguageService, LogService, NotifyService, RbacService, UserService } from '../../../../../core/services';
import { Subject } from 'rxjs';
import { SystemApiService } from '../../http';
import { TimerService } from './timer.service';
import { UpdateModel } from '../../models';

// TODO: Be able to alert people when there are CRITICAL updates available

// :: TODO: Check to see if we have already submitted an alert

@Injectable()
export class UpdatesService implements OnDestroy {
    /**
     *  Define our updates model
     */
    public updates: UpdateModel;

    /**
     * Last time we notified, what was the total number of available updates.
     */
    public lastTotal: number;

    /**
     * Used to inform our other components of our update data
     */
    public updatesEmitter: Subject<UpdateModel> = new Subject<UpdateModel>();

    /**
     * Store pulse status.
     * @private
     */
    private _updateStatus: any;

    /**
     * Stored pulse status for comparison.
     * @private
     */
    private storedUpdateStatus: string;

    /**
     * Stored Timestamp of the last socket event.
     * @private
     */
    private timeStamp: number;

    /**
     * Stored intervalId for use in OnDestroy.
     * @private
     */
    private intervalId: any;

    constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private systemApiService: SystemApiService,
        private timer: TimerService,
        private userService: UserService
    ) {
        this.lastTotal = 0;

        this.onInitialise();
        this.subscribeToUserService();
    }

    onInitialise(): void {
        this.timeStamp = new Date().getTime();
        this.intervalId = setInterval((): void => {
            if (Math.abs(this.timeStamp - new Date().getTime()) > 15000) {
                this.getDataFromApi();
                this.logService.debug('PromptService:onInitialise', 'Socket data out of date, making an API call.');
            }
        }, 15000);
    }

    ngOnDestroy(): void {
        clearInterval(this.intervalId);
    }

    get updateStatus(): UpdateModel {
        return this._updateStatus;
    }

    set updateStatus(updateStatus: UpdateModel) {
        this.timeStamp = new Date().getTime();

        if (this.storedUpdateStatus !== JSON.stringify(updateStatus)) {
            this.storedUpdateStatus = JSON.stringify(updateStatus);

            this.formatUpdateData(updateStatus);
        }
    }

    /**
     * Used to for an update check
     */
    public forceUpdateCheck(): void {
        this.clearNotificationStatus();
        this.stopUpdateChecks();
        this.getDataFromApi();
    }

    /**
     * Stop checking for updates
     */
    public stopUpdateChecks(): void {
        this.logService.debug('UpdatesService:stopUpdateChecks()', 'stopping update check interval');
        this.timer.removeInterval('SystemUpdates');
    }

    /**
     * Get our available updates
     */
    public getUpdates(): UpdateModel {
        return this.updates;
    }

    /**
     * Used to reset whether we have notified the user or not.
     * Note: To be used after a system update.
     */
    public clearNotificationStatus(): void {
        this.lastTotal = 0;
    }

    /**
     * Check for force update permissions.
     * @private
     */
    private checkForceUpdatePermissions(): boolean {
        const forceUpdateReference = {
            'force-update': [{ path: '/system/update/force', methods: ['GET'] }]
        };
        const permissions = this.rbacService.getPermissionsLookup(forceUpdateReference);

        return Object.values(permissions)?.every(item => item);
    }

    /**
     * Use a system alert to inform our logged-in user
     */
    private alertUpdates(total: number): void {
        if (total > this.lastTotal) {
            this.logService.debug('UpdatesService:alertUpdates()', 'system updates available', total);
            this.notifyService.info(`There are ${total} system updates available!`, {
                system: true,
                id: 'notify_system_update',
                url: 'maintenance/software-update'
            });
            this.lastTotal = total;
        } else {
            this.logService.debug('UpdatesService:alertUpdates()', 'no new updates available..');
        }
    }

    /**
     * Get data from API.
     * @private
     */
    private getDataFromApi(): void {
        const hasForcePermissions: boolean = this.checkForceUpdatePermissions();
        this.systemApiService.getUpdates({ timeout: 20000, hasForcePermissions: hasForcePermissions })
            .subscribe({
                    next: (updateStatus: UpdateModel): void => {
                        this.formatUpdateData(updateStatus);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('UpdatesService:getDataFromApi', 'Unable to get data from API', error.message);
                    }
                }
            );
    }

    /**
     * Format data
     * @param updateStatus
     * @private
     */
    private formatUpdateData(updateStatus: UpdateModel): void {
        this._updateStatus = updateStatus;
        const count: number = updateStatus.majorVersion?.length + updateStatus.packages?.length;
        if (updateStatus && count) {
            this.alertUpdates(count);
        }

        const notification: NotificationModel = this.notifyService.getNotificationsById('serviceRestart');
        if (updateStatus.restartRequired && !notification) {
            const message: string = this.languageService.translate('system_update_restart');
            this.notifyService.warning(message, {
                id: 'serviceRestart',
                static: true,
                url: 'maintenance/services',
                urlCloseOnClick: true
            });
        }

        this.updatesEmitter.next(updateStatus);
    }

    /**
     * Subscribe to user service to detect logout.
     * @private
     */
    private subscribeToUserService(): void {
        this.userService.logOutEmitter
            .subscribe({
                next: (state: boolean): void => {
                    if (state) {
                        this.tearDownService();
                    }
                }
            });
    }

    /**
     * Tear down service.
     * @private
     */
    private tearDownService(): void {
        clearInterval(this.intervalId);
    }
}
